import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { ClientError, type TypedDocumentString } from 'graphql-request';
import { GraphQLRequestError } from '~/lib/GraphQLRequestError.ts';
import { graphqlRequest } from '~/lib/graphql-client';
import { extractGraphQLErrorClassifications } from '~/utils';

export const STALE_TIME_MS = 30_000;

export async function runGraphqlQuery<T, V = object>(
  query: TypedDocumentString<T, V>,
  params: V | undefined,
  signal: AbortSignal
): Promise<T> {
  try {
    return await graphqlRequest(query, params as object, signal);
  } catch (error) {
    if (error instanceof ClientError) {
      if (error.response.status === 401 || error.response.status === 403) {
        throw error;
      }
      if (error.response.errors) {
        throw new GraphQLRequestError(error.response.errors);
      }
    }
    throw error;
  }
}

const NON_RETRYABLE_CLASSIFICATIONS = new Set([
  'NOT_FOUND',
  'BAD_REQUEST',
  'FORBIDDEN',
  'UNAUTHORIZED',
]);

export function isNonRetryableGraphqlError(error: Error): boolean {
  if (error instanceof ClientError) {
    const status = error.response.status;
    if (status === 401 || status === 403) return true;
  }
  return extractGraphQLErrorClassifications(error).some((classification) =>
    NON_RETRYABLE_CLASSIFICATIONS.has(classification)
  );
}

export function graphqlQueryRetry(failureCount: number, error: Error) {
  return !isNonRetryableGraphqlError(error) && failureCount < 2;
}

export function useGraphqlQuery<T, V = object>(
  query: TypedDocumentString<T, V>,
  queryKey: readonly unknown[],
  params?: V,
  options?: Partial<UseQueryOptions<T>>
) {
  return useQuery({
    queryKey,
    queryFn: ({ signal }) => runGraphqlQuery(query, params, signal),
    retry: graphqlQueryRetry,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
    ...options,
  });
}
